import React, { useState } from 'react';
import styled from 'styled-components';

import { fetchPlayerStatistics } from './api';
import { useAsyncResource } from './hooks';
import PlayerLoader from '../../components/PlayerLoader';
import { useLexicon } from '../../hooks/useLexicon';

const HEADLINE_PARAM_IDS = [1, 2, 201, 35, 36, 14, 206];

const PlayerStatistics = () => {
  const l = useLexicon();
  const [selectedSeasonId, setSelectedSeasonId] = useState('');

  const { data, loading, error } = useAsyncResource(
    () => fetchPlayerStatistics(selectedSeasonId ? { year_season_id: selectedSeasonId } : {}),
    [selectedSeasonId],
  );

  const payload = data?.data ?? data?.results ?? data;
  const yearSeason = payload?.year_season;
  const availableSeasons = payload?.available_seasons ?? [];
  const matchesPlayed = payload?.matches_played ?? 0;
  const statistics = payload?.statistics ?? [];

  const headlineStats = HEADLINE_PARAM_IDS
    .map((id) => statistics.find((s) => s.parameter_id === id))
    .filter(Boolean);

  const otherStats = statistics.filter((s) => !HEADLINE_PARAM_IDS.includes(s.parameter_id));

  return (
    <Wrap>
      <Title>{l(1696)}</Title>

      {availableSeasons.length > 0 && (
        <SeasonSelect
          value={selectedSeasonId || yearSeason?.id || ''}
          onChange={(e) => setSelectedSeasonId(e.target.value)}
        >
          {availableSeasons.map((s) => (
            <option key={s.id} value={s.id}>{s.years}</option>
          ))}
        </SeasonSelect>
      )}

      {loading && <PlayerLoader label={l(1701)} />}
      {error && <ErrorBox>{l(1697)}</ErrorBox>}

      {!loading && !error && payload && (
        <>
          <SummaryRow>
            <SummaryLabel>{yearSeason?.years ?? l(1698)}</SummaryLabel>
            <SummaryValue>{matchesPlayed} {l(1699)}</SummaryValue>
          </SummaryRow>

          {statistics.length === 0 ? (
            <Muted>{l(1700)}</Muted>
          ) : (
            <>
              <StatsGrid>
                {headlineStats.map((s) => (
                  <StatCard key={s.parameter_id}>
                    <StatValue>{s.count}</StatValue>
                    <StatLabel>{s.parameter_name}</StatLabel>
                  </StatCard>
                ))}
              </StatsGrid>

              {otherStats.length > 0 && (
                <StatsList>
                  {otherStats.map((s) => (
                    <StatRow key={s.parameter_id}>
                      <StatRowLabel>{s.parameter_name}</StatRowLabel>
                      <StatRowValue>{s.count}</StatRowValue>
                    </StatRow>
                  ))}
                </StatsList>
              )}
            </>
          )}
        </>
      )}
    </Wrap>
  );
};

export default PlayerStatistics;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
  margin: 0;
`;

const SeasonSelect = styled.select`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.lines};
  background: ${(props) => props.theme.row};
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
  padding: 8px 0;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 8px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${(props) => props.theme.row};
  border-radius: 12px;
`;

const SummaryLabel = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
`;

const SummaryValue = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;

const StatCard = styled.div`
  background: ${(props) => props.theme.row};
  border-radius: 10px;
  padding: 12px;
`;

const StatValue = styled.div`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 20px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
`;

const StatsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${(props) => props.theme.row};
  border-radius: 8px;
  font-size: 13px;
`;

const StatRowLabel = styled.span`
  color: ${(props) => props.theme.blueish2};
`;

const StatRowValue = styled.span`
  font-family: 'SatoshiBold', sans-serif;
`;
